import { BimActKc2GroupTypeEnum, BimPriceLevelEnum } from 'gql/graphql';
import type { BimActKc2ListItemData, BimActKc2StatusEnum, Maybe, Scalars } from 'gql/graphql';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useProjectData } from 'hooks/useProjectData';
import { ActsKC2ListContainer } from 'pages/Project/Common/ActsKC2ListContainer';

import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BimWorkOrderActsKc2ListTable } from '../BimWorkOrderActsKc2ListTable';
import { BimWorkOrderOtherActKc2Details } from '../BimWorkOrderOtherActsKc2';
import { getBimWorkOrderRowClass } from '../BimWorkOrderPriceTable/cellRenderers';
import { useGetBimWorkOrderDataFromProject } from '../hooks';

import { BimWorkOrderActsKc2Details } from './BimWorkOrderActsKc2Details/BimWorkOrderActsKc2Details';

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
};

const tabElements = [
    { label: 'Все', id: null },
    { label: 'По месяцу', id: BimActKc2GroupTypeEnum.ByMonth },
    { label: 'По году', id: BimActKc2GroupTypeEnum.ByYear },
    { label: 'По виду строительства', id: BimActKc2GroupTypeEnum.ByConstructionType },
    { label: 'По группе затрат', id: BimActKc2GroupTypeEnum.ByCostGroup },
    { label: 'По объекту', id: BimActKc2GroupTypeEnum.ByObject },
    { label: 'Ручная группировка', id: BimActKc2GroupTypeEnum.Custom },
];

type TTabElement = (typeof tabElements)[0];

export interface IActKc2OtherItem {
    date?: Maybe<Scalars['DateTime']>;
    id: Scalars['Long'];
    number?: number | undefined;
    status: Maybe<BimActKc2StatusEnum> | undefined;
    isInactive?: Maybe<Scalars['Boolean']>;
}

export const BimWorkOrderActsKc2List = () => {
    const { currentOrder, workOrderTitle } = useGetBimWorkOrderDataFromProject({});
    const [actKc2OtherForDetails, setActKc2OtherForDetails] = useState<IActKc2OtherItem | null>(null);
    const [isShowDetailActKc2Smr, setShowDetailActKc2Smr] = useState<Partial<BimActKc2ListItemData> | null>(null);
    const [activeGroupType, setActiveGroupType] = useState<TTabElement | null>(null);

    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const handeGroupTypeChange = useCallback((value: TTabElement) => {
        setActiveGroupType(value);
    }, []);

    const onActKc2OtherDetailsClose = useCallback(() => {
        setActKc2OtherForDetails(null);
    }, []);

    const onOpenOtherActKc2Details = useCallback((act: IActKc2OtherItem) => {
        setActKc2OtherForDetails(act);
    }, []);

    const onActsClose = useCallback(() => {
        setShowDetailActKc2Smr(null);
    }, []);

    const onOpenActsSmrDetail = useCallback((data: Partial<BimActKc2ListItemData>) => {
        setShowDetailActKc2Smr(data);
    }, []);

    const { workOrderStatus } = useGetBimWorkOrderDataFromProject({});

    const statusProject = projectData?.status;
    const contractStatus = projectData?.contractNavigation?.data?.contractNavData?.status;

    const { savedFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                customSaveKey: `${projectId}_priceLevel`,
                withEmptyFilterSetter: (localSaver) => {
                    localSaver({
                        key: 'priceLevel',
                        newState: BimPriceLevelEnum.BasicPriceLevel,
                    });
                },
            }),
            [projectId],
        ),
    );

    return (
        <ActsKC2ListContainer
            workOrderTitle={workOrderTitle}
            currentOrderId={currentOrder?.id}
            activeTabItem={activeGroupType}
            tabElements={tabElements}
            handleGroupChange={handeGroupTypeChange}
        >
            <BimWorkOrderActsKc2ListTable
                onOpenOtherActKc2Details={onOpenOtherActKc2Details}
                onOpenActsSmrDetail={onOpenActsSmrDetail}
                workOrderId={currentOrder?.id}
                activeGroupTypeId={activeGroupType?.id}
            />
            {!!actKc2OtherForDetails && (
                <BimWorkOrderOtherActKc2Details
                    actKC2={actKc2OtherForDetails}
                    onClose={onActKc2OtherDetailsClose}
                    defaultColDef={defaultColDef}
                    styleClass="ActKC2Details"
                    isActionDisabled
                />
            )}
            {!!isShowDetailActKc2Smr && (
                <BimWorkOrderActsKc2Details
                    onClose={onActsClose}
                    actKc2Smr={isShowDetailActKc2Smr}
                    rowClassRules={getBimWorkOrderRowClass}
                    defaultColDef={defaultColDef}
                    styleClass="BimWorkOrderPriceDetails customHeaderColor"
                    workOrderStatus={workOrderStatus}
                    contractStatus={contractStatus}
                    statusProject={statusProject}
                    isActionDisabled
                    isReadOnly
                    priceLevel={savedFilterState?.priceLevel}
                />
            )}
        </ActsKC2ListContainer>
    );
};

BimWorkOrderActsKc2List.displayName = 'BimWorkOrderActsKc2List';
